import Big from 'big.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import FractionalShares from '@/modules/issuer-dashboard/components/FractionalShares.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { usePaymentSetCompletedMutation } from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  Button,
  CalendarInput,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  TextInputField,
} from 'ui';
import { PaymentSetCompletedStatusRequest, RoundTo } from 'api';

dayjs.extend(utc);

const validationSchema = yup.object({
  amount: yup.number().required('This field is required'),
  // .moreThan(0, 'Ensure this value is greater than 0'),
  confirmation_date: yup.string().trim().required('This field is required'),
  do_not_notify_investor: yup.boolean().required('This select is required'),
});

const initialValues = {
  id: 0,
  confirmation_date: '',
  memo: '',
  amount: 0,
  round_to: RoundTo.Up,
  is_need_complete: true,
};

export interface IssuerConfirmPaymentModalProps {
  payment_id: number;
  amount: number;
  price_per_share: number;
  open: boolean;
  onClose: () => void;
}

export const IssuerConfirmPaymentModal = ({
  open,
  amount,
  payment_id,
  price_per_share,
  onClose,
}: IssuerConfirmPaymentModalProps) => {
  const allowsFractionalShares = useGetRegister()?.allows_fractional_shares ?? true;

  const formik = useFormik<Required<PaymentSetCompletedStatusRequest>>({
    initialValues: {
      ...initialValues,
      id: payment_id,
      confirmation_date: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]'),
      amount: +new Big(amount).round(2, BigRoundMode.roundHalfUp).valueOf(),
      do_not_notify_investor: false,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await completePayment(values as PaymentSetCompletedStatusRequest);
      resetForm();
      onClose();
    },
    // enableReinitialize: true,
  });
  const { setFieldValue } = formik;

  const { mutateAsync: completePayment, isLoading } = usePaymentSetCompletedMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    if (!date) {
      formik.setFieldValue('confirmation_date', '');
      formik.setFieldError('confirmation_date', '');
      return;
    }
    const currentDate = date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');
    formik.setFieldValue('confirmation_date', currentDate);
    formik.setFieldError('confirmation_date', '');
  };

  const handleChangeAmount = (amount: number | string) => {
    setFieldValue('amount', amount);
  };

  const handleChangeSelect =
    (field: keyof PaymentSetCompletedStatusRequest) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    };

  const amountExcess = useMemo(
    () =>
      formik?.values?.amount && price_per_share
        ? Number(new Big(formik?.values?.amount || 0).mod(price_per_share).valueOf())
        : 0,
    [formik?.values?.amount, price_per_share],
  );

  const isAmountFractional = amountExcess !== 0;

  const roundDownShares = useMemo(
    () =>
      formik?.values?.amount && price_per_share
        ? Number(
            new Big(formik.values.amount)
              .div(price_per_share)
              .round(0, BigRoundMode.roundDown)
              .valueOf(),
          )
        : 0,
    [formik.values.amount, price_per_share],
  );

  const roundUpShares = useMemo(
    () =>
      formik?.values?.amount && price_per_share
        ? Number(
            new Big(formik.values.amount)
              .div(price_per_share)
              .round(0, BigRoundMode.roundUp)
              .valueOf(),
          )
        : 0,
    [formik.values.amount, price_per_share],
  );

  return (
    <ModalContentWrapper open={open} title="Confirm payment" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <CurrencyInput
              label="amount"
              error={formik.errors.amount}
              value={formik.values.amount}
              onChange={handleChangeAmount}
            />

            <CalendarInput
              label="Payment date"
              name="confirmation_date"
              onChange={handleChangeDate}
              error={formik.errors.confirmation_date}
              format="DD-MM-YYYY HH:mm"
              value={
                formik.values.confirmation_date ? dayjs(formik.values.confirmation_date) : undefined
              }
              showTime={true}
            />
            <TextInputField
              textarea
              name="memo"
              placeholder="Memo..."
              label="Memo (optional)"
              className="resize-none"
            />
            <CustomCheckbox
              label="Do not notify investor"
              checked={formik.values.do_not_notify_investor}
              onChange={e => handleChangeSelect('do_not_notify_investor')(e.target.checked)}
            />
            {!allowsFractionalShares && isAmountFractional && (
              <FractionalShares
                amount={formik.values.amount}
                price_per_share={price_per_share}
                amountExcess={amountExcess}
                value={formik.values.round_to}
                roundDownShares={roundDownShares}
                roundUpShares={roundUpShares}
                onChange={value => formik.setFieldValue('round_to', value)}
              />
            )}
            <Button onClick={formik.submitForm} disabled={isLoading}>
              Confirm
            </Button>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
